import React, { useCallback, useEffect, useState } from 'react';
import opensearchService from '../services/opensearchService';
import { formatDateTime } from '../utils/datetime';
import './AuditTrail.css';

// Admin ▸ Platform ▸ Audit Trail — the cross-product record of who changed (or read) what, where. One search over the
// live trail (OpenSearch, ~13 months) and its Object-Locked S3 archive (older) — the API merges them (audit-archive-search.js).
//
// Events are written by /opt/utils/audit (`audit.record(...)`) from any lambda and stored in
// audit-trail-YYYY.MM. Every filter here is a term match on an indexed keyword, so this stays cheap as the
// trail grows; the free-text box deliberately only searches the human summary — structured questions are
// answered by the filters.
//
// The per-event "changed fields" are the point of the screen: the question that motivated it was
// "what silently switched this payment porter's schedule off", which is a changed_fields = schedule_enabled
// filter. The same data backs the Payment Porter history tab.

const EMPTY = {
    q: '', product: '', productId: '', action: '', userEmail: '', entity: '', changedField: '',
    partnerId: '', accountId: '', hotelId: '', from_date: '', to_date: '',
};

const PAGE = 50;

const ACTION_CLASS = {
    CREATE: 'at-badge-create', UPDATE: 'at-badge-update', DELETE: 'at-badge-delete',
    RUN: 'at-badge-run', IMPORT: 'at-badge-run', NOTIFY: 'at-badge-run',
    // Someone looked at guest data (a vault search, a folio, a download) — audit.ACTIONS.READ.
    READ: 'at-badge-read',
};

// Values are stored as strings; show something readable for the empty ones.
const val = (v) => (v === null || v === undefined || v === '' ? <span className="at-empty">(empty)</span> : String(v));

const ChangeRows = ({ changes }) => (
    <table className="at-changes">
        <thead><tr><th>Field</th><th>From</th><th>To</th></tr></thead>
        <tbody>
            {changes.map((c, i) => (
                <tr key={i}>
                    <td className="at-changes-field">{c.field}</td>
                    <td className="at-changes-from">{val(c.from)}</td>
                    <td className="at-changes-to">{val(c.to)}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const Scope = ({ e }) => {
    const parts = [
        e.partner_id && `Partner ${e.partner_id}`,
        e.account_id && `Chain ${e.account_id}`,
        e.hotel_id   && `Hotel ${e.hotel_id}`,
    ].filter(Boolean);
    return parts.length ? <span className="at-scope">{parts.join(' · ')}</span> : <span className="at-empty">—</span>;
};

const AuditTrail = () => {
    const [filters, setFilters]   = useState(EMPTY);
    const [applied, setApplied]   = useState(EMPTY);
    const [events, setEvents]     = useState([]);
    const [total, setTotal]       = useState(0);
    const [page, setPage]         = useState(0);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [facets, setFacets]     = useState({ products: [], actions: [], users: [], entities: [], changedFields: [] });
    const [expanded, setExpanded] = useState(null);

    const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

    const load = useCallback(async (f, pageNo) => {
        setLoading(true);
        setError(null);
        try {
            const params = { size: PAGE, from: pageNo * PAGE };
            Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
            const res = await opensearchService.searchAudit(params);
            setEvents(res.data?.events || []);
            setTotal(res.data?.total || 0);
        } catch (err) {
            setError(err.response?.data?.error?.message || err.message || 'Failed to load the audit trail');
            setEvents([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(applied, page); }, [load, applied, page]);

    // Facets populate the dropdowns from what actually exists, so the lists can't drift from the data.
    useEffect(() => {
        opensearchService.getAuditFacets()
            .then(res => setFacets({
                products: res.data?.products || [], actions: res.data?.actions || [],
                users: res.data?.users || [], entities: res.data?.entities || [],
                changedFields: res.data?.changedFields || [],
            }))
            .catch(() => { /* filters still work as free text */ });
    }, []);

    const search = () => { setPage(0); setApplied(filters); };
    const reset  = () => { setFilters(EMPTY); setPage(0); setApplied(EMPTY); };

    const onKeyDown = (e) => { if (e.key === 'Enter') search(); };

    const opts = (list) => list.map(o => (
        <option key={o.value} value={o.value}>{o.value} ({o.count})</option>
    ));

    const activeFilters = Object.entries(applied).filter(([, v]) => v).length;
    const lastPage = Math.max(0, Math.ceil(total / PAGE) - 1);

    return (
        <div className="at-page">
            <div className="at-header">
                <div>
                    <h1 className="at-title">Audit Trail</h1>
                    <p className="at-subtitle">
                        Who changed what, across every product. Written by <code>/opt/utils/audit</code> and kept for 400 days
                        in <code>audit-trail-YYYY.MM</code>.
                    </p>
                </div>
                <div className="at-count">
                    {loading ? 'Loading…' : `${total.toLocaleString()} event${total === 1 ? '' : 's'}`}
                    {activeFilters > 0 && <span className="at-count-filtered"> · {activeFilters} filter{activeFilters === 1 ? '' : 's'}</span>}
                </div>
            </div>

            <div className="at-filters">
                <div className="at-filter at-filter-wide">
                    <label>Search</label>
                    <input value={filters.q} onChange={e => set('q', e.target.value)} onKeyDown={onKeyDown}
                           placeholder="Summary, product or user…" />
                </div>
                <div className="at-filter">
                    <label>Product</label>
                    <select value={filters.product} onChange={e => set('product', e.target.value)}>
                        <option value="">All</option>{opts(facets.products)}
                    </select>
                </div>
                <div className="at-filter at-filter-narrow">
                    <label>Product ID</label>
                    <input value={filters.productId} onChange={e => set('productId', e.target.value)} onKeyDown={onKeyDown} placeholder="e.g. 2" />
                </div>
                <div className="at-filter">
                    <label>Action</label>
                    <select value={filters.action} onChange={e => set('action', e.target.value)}>
                        <option value="">All</option>{opts(facets.actions)}
                    </select>
                </div>
                <div className="at-filter">
                    <label>User</label>
                    <select value={filters.userEmail} onChange={e => set('userEmail', e.target.value)}>
                        <option value="">All</option>{opts(facets.users)}
                    </select>
                </div>
                <div className="at-filter">
                    <label>Entity</label>
                    <select value={filters.entity} onChange={e => set('entity', e.target.value)}>
                        <option value="">All</option>{opts(facets.entities)}
                    </select>
                </div>
                <div className="at-filter">
                    <label>Changed field</label>
                    <select value={filters.changedField} onChange={e => set('changedField', e.target.value)}>
                        <option value="">Any</option>{opts(facets.changedFields)}
                    </select>
                </div>
                <div className="at-filter at-filter-narrow">
                    <label>Partner</label>
                    <input value={filters.partnerId} onChange={e => set('partnerId', e.target.value)} onKeyDown={onKeyDown} placeholder="id" />
                </div>
                <div className="at-filter at-filter-narrow">
                    <label>Chain</label>
                    <input value={filters.accountId} onChange={e => set('accountId', e.target.value)} onKeyDown={onKeyDown} placeholder="id" />
                </div>
                <div className="at-filter at-filter-narrow">
                    <label>Hotel</label>
                    <input value={filters.hotelId} onChange={e => set('hotelId', e.target.value)} onKeyDown={onKeyDown} placeholder="id" />
                </div>
                {/* Dates are yyyy-mm-dd throughout the portal; the range is inclusive and UTC, matching how
                    the events are stamped. */}
                <div className="at-filter at-filter-narrow">
                    <label>From (UTC)</label>
                    <input type="date" value={filters.from_date} onChange={e => set('from_date', e.target.value)} />
                </div>
                <div className="at-filter at-filter-narrow">
                    <label>To (UTC)</label>
                    <input type="date" value={filters.to_date} onChange={e => set('to_date', e.target.value)} />
                </div>
                <div className="at-filter-actions">
                    <button className="at-btn at-btn-primary" onClick={search} disabled={loading}>Search</button>
                    <button className="at-btn" onClick={reset} disabled={loading}>Reset</button>
                </div>
            </div>

            {error && <div className="at-error">{error}</div>}

            <div className="at-table-wrap">
                <table className="at-table">
                    <thead>
                        <tr>
                            <th style={{ width: '10rem' }}>When</th>
                            <th style={{ width: '13rem' }}>User</th>
                            <th style={{ width: '12rem' }}>Product</th>
                            <th style={{ width: '6rem' }}>Action</th>
                            <th>What changed</th>
                            <th style={{ width: '11rem' }}>Scope</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="at-msg">Loading…</td></tr>}
                        {!loading && events.length === 0 && (
                            <tr><td colSpan={6} className="at-msg">
                                {activeFilters ? 'No events match these filters.' : 'No audit events recorded yet.'}
                            </td></tr>
                        )}
                        {!loading && events.map((e) => {
                            const isOpen = expanded === e.id;
                            const changes = Array.isArray(e.changes) ? e.changes : [];
                            return (
                                <React.Fragment key={e.id}>
                                    <tr className={`at-row${isOpen ? ' at-row-open' : ''}`}
                                        onClick={() => setExpanded(isOpen ? null : e.id)}>
                                        <td className="at-when">{formatDateTime(e.timestamp)}</td>
                                        <td>
                                            <div className="at-user">{e.user_email || e.user_name || <span className="at-empty">system</span>}</div>
                                            {e.is_super_admin && <span className="at-sa">superadmin</span>}
                                        </td>
                                        <td>
                                            <span className="at-product">{e.product}</span>
                                            {e.product_id && <span className="at-productid">#{e.product_id}</span>}
                                            {e.product_name && <div className="at-productname">{e.product_name}</div>}
                                        </td>
                                        <td><span className={`at-badge ${ACTION_CLASS[e.action] || 'at-badge-other'}`}>{e.action}</span></td>
                                        <td>
                                            {e.summary && <div className="at-summary">{e.summary}</div>}
                                            {e.entity && <span className="at-entity">{e.entity}{e.entity_id ? ` #${e.entity_id}` : ''}</span>}
                                            {(e.changed_fields || []).slice(0, 6).map(f => (
                                                <span key={f} className="at-field">{f}</span>
                                            ))}
                                            {(e.changed_fields || []).length > 6 && (
                                                <span className="at-field">+{e.changed_fields.length - 6} more</span>
                                            )}
                                        </td>
                                        <td><Scope e={e} /></td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="at-detail-row">
                                            <td colSpan={6}>
                                                {changes.length > 0
                                                    ? <ChangeRows changes={changes} />
                                                    : <div className="at-msg">No field-level changes recorded for this event.</div>}
                                                <div className="at-meta">
                                                    {e.source && <span>source <code>{e.source}</code></span>}
                                                    {e.method && e.path && <span>{e.method} <code>{e.path}</code></span>}
                                                    {e.ip && <span>from {e.ip}</span>}
                                                    {e.request_id && <span>request <code>{e.request_id}</code></span>}
                                                    <span>index <code>{e.index}</code></span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {total > PAGE && (
                <div className="at-pager">
                    <button className="at-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading}>Previous</button>
                    <span className="at-pager-info">
                        {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}
                    </span>
                    <button className="at-btn" onClick={() => setPage(p => p + 1)} disabled={page >= lastPage || loading}>Next</button>
                </div>
            )}
        </div>
    );
};

export default AuditTrail;
